/**
 * Movement system: advances units along their paths in id order on a live
 * board of units and buildings, at the pace of their group; a step blocked by
 * an enemy waits, then attacks or re-routes. Ends with `settle`, which puts
 * units that stopped onto free tiles or inside buildings.
 */
import { getHexTileMapKey } from '../../hexmap/model/keys';
import type { HexTileMapKey } from '../../hexmap/model/types';
import { findPathToNearest } from '../../hexmap/pathfinding';
import { tickDerived } from '../derived';
import { invalidateDerived } from '../derivedRegistry';
import { MILLI, toMilli } from '../fixed';
import { settle, unitsByMint } from '../occupancy';
import { redirect } from '../redirect';
import { isHidden } from '../spies';
import type { Building, Cell, EntityId, SimContext, SimState, SystemFn, Unit } from '../types';
import { attackTargetOf, canAttack, canHit } from './pursuit';

/**
 * Units with a path at the start of each tick (recorded by `recordInFlight`,
 * first in the pipeline), so `settle` can tell a unit that just stopped (e.g.
 * automation dropped its path mid-route) from one already standing. Derived
 * from the state at tick start, so it's deterministic and snapshot-safe.
 */
const inFlight = new WeakMap<SimState, Set<EntityId>>();

const recordInFlight: SystemFn = (state) => {
  const ids = new Set<EntityId>();
  for (const u of Object.values(state.units)) if (u.path && u.path.length > 0) ids.add(u.id);
  inFlight.set(state, ids);
};

/**
 * How long (ticks) a unit waits at a step an enemy unit or rival building
 * blocks before it re-routes around it or, if it can hit it, attacks it.
 */
const BLOCK_WAIT_TICKS = 10;

/** What stands in the way on a tile: an enemy unit or a rival building. */
type Blocker = { id: EntityId; isUnit: boolean };

/**
 * Who is where, kept up to date as units step: units outside buildings by
 * tile (mint order), and every building tile. Hidden units (spies.ts) are
 * ghosts: they aren't on the board and units don't block them.
 */
class Board {
  private readonly units = new Map<HexTileMapKey, Unit[]>();
  private readonly buildings: ReadonlyMap<HexTileMapKey, Building>;
  private readonly ghosts = new Set<Unit>();

  constructor(state: SimState, ctx: SimContext, ordered: readonly Unit[]) {
    this.buildings = tickDerived(state, ctx).buildingIndex();
    for (const u of ordered) {
      if (isHidden(ctx.rules, u)) this.ghosts.add(u);
      else if (!u.inside) this.add(u);
    }
  }

  add(u: Unit): void {
    if (this.ghosts.has(u)) return;
    const key = getHexTileMapKey(u.pos);
    const list = this.units.get(key);
    if (list) list.push(u);
    else this.units.set(key, [u]);
  }

  remove(u: Unit): void {
    const list = this.units.get(getHexTileMapKey(u.pos));
    const i = list?.indexOf(u) ?? -1;
    if (list && i >= 0) list.splice(i, 1);
  }

  /** What blocks `u` from entering `cell`: a rival building on it, else its oldest enemy unit. */
  blocker(u: Unit, cell: Cell): Blocker | undefined {
    const key = getHexTileMapKey(cell);
    const b = this.buildings.get(key);
    if (b && b.owner !== u.owner) return { id: b.id, isUnit: false };
    if (this.ghosts.has(u)) return undefined;
    const enemy = this.units.get(key)?.find((o) => o.owner !== u.owner);
    return enemy ? { id: enemy.id, isUnit: true } : undefined;
  }

  /** Every tile `u` can't enter right now. */
  blockedFor(u: Unit): Set<HexTileMapKey> {
    const blocked = new Set<HexTileMapKey>();
    for (const [key, b] of this.buildings) if (b.owner !== u.owner) blocked.add(key);
    if (this.ghosts.has(u)) return blocked;
    for (const [key, list] of this.units) {
      if (list.some((o) => o.owner !== u.owner)) blocked.add(key);
    }
    return blocked;
  }
}

/**
 * A unit that has waited BLOCK_WAIT_TICKS at a blocked step: a fighter on an
 * attack order whose target still stands re-routes round the blocker when it
 * can, keeping its target (so artillery sent at a city isn't pulled off onto
 * a passing unit); otherwise a fighter that can hit the blocker attacks it
 * (pursuit and combat take over); anyone else re-routes to where it was going
 * around everything in the way (or as close as it can get; already there, it
 * stops). A step under way is turned back rather than dropped (redirect.ts).
 * Returns whether it stopped.
 */
function unblock(
  state: SimState,
  ctx: SimContext,
  board: Board,
  unit: Unit,
  blocker: Blocker
): boolean {
  // Its wait (`blocked`) tells redirect the step can't be finished or turned
  // back onto the blocker's tile, so a new way drops it. Giving up stops it
  // outright (redirect would hold it on the step, still waiting).
  const reroute = (route: Cell[]) => {
    if (route.length <= 1) {
      delete unit.path;
      delete unit.blocked;
      unit.progress = 0;
      return true;
    }
    board.remove(unit);
    redirect(state, unit, route);
    board.add(unit);
    delete unit.blocked;
    return !unit.path;
  };
  const path = unit.path ?? [];
  const terrain = ctx.rules.units[unit.type]?.terrain ?? [];
  if (
    unit.order?.kind === 'attack' &&
    unit.order.targetId !== blocker.id &&
    attackTargetOf(state, ctx.rules, unit.order.targetId)
  ) {
    const dest = path[path.length - 1];
    const route = findPathToNearest(ctx.terrain, unit.pos, dest, terrain, board.blockedFor(unit));
    if (route && route.length > 1) {
      reroute(route);
      return false;
    }
  }
  // A unit with a stance never starts a fight (a hold unit would then chase
  // the blocker): it goes round like a non-fighter.
  if (!unit.stance && canAttack(ctx.rules, unit) && canHit(ctx.rules, unit.type, blocker.isUnit)) {
    // A guard keeps its tag, so guard.ts still holds it on its leash; a
    // pursuer keeps the target it was sent at, to go back to (pursuit.ts).
    const prev = unit.order?.kind === 'attack' ? unit.order : undefined;
    const resume =
      prev && (prev.resume ?? (prev.targetId !== blocker.id ? prev.targetId : undefined));
    // An attack-move still walks on to its destination afterwards.
    const onward = prev
      ? prev.onward
      : unit.order?.kind === 'attackMove'
        ? unit.order.target
        : undefined;
    unit.order = { kind: 'attack', targetId: blocker.id };
    if (onward) unit.order.onward = onward;
    // The focus queue waits behind the blocker fight, with the target it resumes.
    if (prev?.queue) unit.order.queue = prev.queue;
    // Fights the blocker at its own speed, not the pace of the units it was sent with.
    delete unit.pace;
    if (prev?.guard !== undefined) unit.order.guard = prev.guard;
    if (resume !== undefined && resume !== blocker.id) unit.order.resume = resume;
    return reroute([unit.pos]);
  }
  const dest = path[path.length - 1];
  const route = findPathToNearest(ctx.terrain, unit.pos, dest, terrain, board.blockedFor(unit));
  if (!route) {
    delete unit.blocked;
    return false;
  }
  return reroute(route);
}

/** What a group member is doing for pace purposes: the attack target it shares, or travelling with the group. */
function paceKey(u: Unit): string {
  return `${u.groupId}|${u.order?.kind === 'attack' ? u.order.targetId : ''}`;
}

/**
 * Group id + task (`paceKey`) → the speed its members travel at: the slowest
 * `speedMultiplier` of those doing the same thing (on the same attack
 * target, or travelling with the group; formation sits attackers out), not
 * inside a building, not automated, so a group keeps together (cavalry don't
 * reach the walls long before the rams). A unit that leaves the group moves
 * at its own speed again.
 */
function groupPaces(state: SimState, ctx: SimContext): Map<string, number> {
  const paces = new Map<string, number>();
  if (!state.groups) return paces;
  for (const u of Object.values(state.units)) {
    if (u.groupId === undefined || u.inside || u.auto) continue;
    const speed = ctx.rules.units[u.type]?.speedMultiplier;
    if (speed === undefined) continue;
    const key = paceKey(u);
    paces.set(key, Math.min(paces.get(key) ?? speed, speed));
  }
  return paces;
}

/**
 * `unit`'s speed this tick: its own, capped by the pace of the order it was
 * given with others (`Unit.pace`) and by its group's pace while it moves with
 * one. An automated unit moves at its own.
 */
function paceOf(ctx: SimContext, paces: ReadonlyMap<string, number>, unit: Unit): number {
  const own = ctx.rules.units[unit.type]?.speedMultiplier ?? 0;
  if (unit.auto) return own;
  let speed = unit.pace === undefined ? own : Math.min(own, unit.pace / MILLI);
  if (unit.groupId !== undefined) speed = Math.min(speed, paces.get(paceKey(unit)) ?? speed);
  return speed;
}

/**
 * Movement system: advances all units with an active path each tick.
 *
 * progress tracks the lerp toward path[0] (the next cell), in thousandths
 * of a step (fixed.ts). At a whole step the unit steps onto that cell, progress carries over,
 * and we check the next step. On arrival (path exhausted) the path key is
 * deleted and progress is reset to 0.
 *
 * The path array holds remaining cells EXCLUDING the current pos, next-first.
 * NEVER stored as empty array: arrival deletes the key.
 *
 * Units move in id order, a group's members at its slowest member's pace
 * (`groupPaces`), units ordered together at the slowest one's (`Unit.pace`). Own units pass through each other and own
 * buildings, but a step onto an enemy unit's tile or a rival building's is
 * blocked: the unit waits there (`Unit.blocked` counts the ticks), then
 * after BLOCK_WAIT_TICKS attacks the blocker or re-routes (`unblock`). Then
 * `settle` (occupancy.ts) makes every still unit fit: one unit per tile,
 * nobody standing on a building (inside, or step off to the nearest free
 * tile).
 */
const movement: SystemFn = (state: SimState, ctx: SimContext, _inputs, dt: number): void => {
  const stopped = new Set<EntityId>();
  const ordered = unitsByMint(state);
  const board = new Board(state, ctx, ordered);
  const paces = groupPaces(state, ctx);
  for (const unit of ordered) {
    if (!unit.path || unit.path.length === 0) {
      // Arrived (an attack order keeps it while pursuit plans more paths).
      if (unit.pace !== undefined && unit.order?.kind !== 'attack') delete unit.pace;
      continue;
    }

    const unitDef = ctx.rules.units[unit.type];
    if (!unitDef) continue;

    const blocker = board.blocker(unit, unit.path[0]);
    if (blocker) {
      unit.blocked = (unit.blocked ?? 0) + 1;
      if (unit.blocked >= BLOCK_WAIT_TICKS && unblock(state, ctx, board, unit, blocker)) {
        if (unit.order?.kind === 'move' || unit.order?.kind === 'attackMove') delete unit.order;
        stopped.add(unit.id);
      }
      continue;
    }
    delete unit.blocked;

    // Thousandths of a step (fixed.ts).
    const step = toMilli(ctx.rules.pacing.baseSpeed * paceOf(ctx, paces, unit) * dt);
    unit.progress += step;

    while (unit.progress >= MILLI && unit.path.length > 0) {
      // A further step this tick that's blocked: wait on this tile.
      if (board.blocker(unit, unit.path[0])) {
        unit.progress = 0;
        break;
      }
      const next = unit.path.shift();
      if (next) {
        board.remove(unit);
        unit.pos = next;
        board.add(unit);
      }
      unit.progress -= MILLI;
      if (unit.path.length === 0) {
        delete unit.path;
        unit.progress = 0;
        if (unit.order?.kind === 'move' || unit.order?.kind === 'attackMove') delete unit.order;
        stopped.add(unit.id);
        break;
      }
    }
  }
  for (const id of inFlight.get(state) ?? []) {
    const u = state.units[id];
    if (u && !u.path) stopped.add(id);
  }
  inFlight.delete(state);
  settle(state, ctx, stopped);
  // Units have moved: what the tick worked out from where they stood is stale.
  invalidateDerived(state);
};

export { BLOCK_WAIT_TICKS, groupPaces, movement, paceKey, recordInFlight };
